import { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
} from "@mui/material";
import { Download as DownloadIcon, FolderZip as FolderZipIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import DashboardLayout from "@/components/DashboardLayout";
import { getDatensicherungList, downloadDatensicherungZip, DatensicherungInfo } from "@/api/datensicherung";

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const Datensicherung = () => {
  const [downloadingDatum, setDownloadingDatum] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data: backups, isLoading, isError } = useQuery<DatensicherungInfo[]>({
    queryKey: ["datensicherung"],
    queryFn: getDatensicherungList,
  });

  const handleDownload = async (datum: string) => {
    setDownloadingDatum(datum);
    setError(null);
    try {
      await downloadDatensicherungZip(datum);
      setSuccess(`Datensicherung ${datum} wird heruntergeladen.`);
    } catch (err: any) {
      setError(err?.message || "Fehler beim Herunterladen der Datensicherung.");
    } finally {
      setDownloadingDatum(null);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 900, mx: "auto", mt: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Datensicherung
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Täglich wird beim ersten Speichern eines Patienten automatisch eine Datensicherung erstellt.
          Jede Sicherung enthält JSON-Dateien für Kunden, Benutzer, Material, Hersteller und Patienten.
          Sie können jede Sicherung als ZIP-Archiv herunterladen.
        </Typography>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Fehler beim Laden der Datensicherungen.
          </Alert>
        )}

        {!isLoading && !isError && backups && backups.length === 0 && (
          <Paper sx={{ p: 4, textAlign: "center", mt: 2 }}>
            <FolderZipIcon sx={{ fontSize: 64, color: "text.disabled", mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Noch keine Datensicherungen vorhanden.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Die erste Sicherung wird beim nächsten Speichern eines Patienten erstellt.
            </Typography>
          </Paper>
        )}

        {!isLoading && !isError && backups && backups.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Datum</strong></TableCell>
                  <TableCell><strong>Enthaltene Dateien</strong></TableCell>
                  <TableCell><strong>Größe</strong></TableCell>
                  <TableCell align="center"><strong>Download</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.map((backup) => (
                  <TableRow key={backup.datum} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {backup.datum}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {backup.dateien.map((datei) => (
                          <Chip
                            key={datei}
                            label={datei}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem" }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatBytes(backup.gesamtgroesseBytes)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={`ZIP herunterladen (${backup.datum})`}>
                        <span>
                          <IconButton
                            color="primary"
                            onClick={() => handleDownload(backup.datum)}
                            disabled={downloadingDatum === backup.datum}
                          >
                            {downloadingDatum === backup.datum ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DownloadIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default Datensicherung;
